import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;
    if (!videoElement) return;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    // Chrome requires muted + playsinline for reliable autoplay
    videoElement.muted = true;
    videoElement.playsInline = true;

    videoElement.load();

    // Chrome often ignores autoplay when src is set dynamically; call play() when ready
    const tryPlay = () => {
      videoElement.play().catch(() => {});
    };

    if (videoElement.readyState >= 3) {
      tryPlay();
    } else {
      videoElement.addEventListener('canplay', tryPlay, { once: true });
      // Fallback: loadeddata fires earlier; some Chrome versions need this
      videoElement.addEventListener('loadeddata', tryPlay, { once: true });
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
